import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { API_BASE, publicMediaUrl, publicReportPdfUrl } from '@/lib/api';
import { Spinner, PriorityChip } from '@/components/Common';
import {
  ShieldCheckIcon,
  DownloadSimpleIcon,
  CheckCircleIcon,
  WarningIcon,
  XCircleIcon,
  PhoneIcon,
  EnvelopeSimpleIcon,
  VideoCameraIcon,
  FileIcon,
  FilePdfIcon,
  FileXlsIcon,
  FileDocIcon,
  FileCsvIcon,
} from '@phosphor-icons/react';
import { QRCodeSVG } from 'qrcode.react';

export default function PublicReportPage() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: null, data: null });

  useEffect(() => {
    axios
      .get(`${API_BASE}/public/reports/${token}`)
      .then((r) => setState({ loading: false, error: null, data: r.data }))
      .catch((e) => setState({ loading: false, error: e.response?.status === 404 ? 'Report not available' : 'Unable to load report', data: null }));
  }, [token]);

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner label="Loading report…" />
      </div>
    );
  }
  if (state.error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-8 text-center">
        <ShieldCheckIcon size={48} color="var(--gl-dark)" weight="fill" />
        <div className="font-display font-extrabold uppercase text-3xl mt-3" style={{ color: 'var(--gl-dark)' }}>
          {state.error}
        </div>
        <p className="mt-2 text-sm" style={{ color: 'var(--gl-muted)' }}>
          This report link may be expired or deactivated. Please contact GigLine.
        </p>
      </div>
    );
  }

  const { report, engagement, findings, gap_items, media, settings, attachments = [] } = state.data;
  const media_index = Object.fromEntries((media || []).map((m) => [m.id, m]));

  return (
    <div className="min-h-screen" style={{ background: 'var(--gl-bg)' }}>
      {/* Cover */}
      <div className="gl-dark-grain px-6 py-8 md:py-14 text-white relative overflow-hidden">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <img src="/gigline-logo-light.png?v=9" alt="GigLine Safety & Compliance" className="w-96 max-w-full h-auto" />
          </div>
          <div className="gl-label" style={{ color: 'var(--gl-gold)' }}>
            {report.kind === 'walkthrough' ? 'Walkthrough · Field Audit' : report.kind === 'gap_check' ? 'Documentation · Gap Check' : report.kind === 'incident_investigation' ? 'Incident · Post-Event Review' : 'Walkthrough + Documentation · Field Report'}
          </div>
          <h1 className="font-display font-black uppercase text-4xl md:text-6xl leading-none mt-1">
            {engagement.business_name}
          </h1>
          <div className="mt-3 text-sm md:text-base" style={{ color: '#cbd5e1' }}>
            {[engagement.site_address, engagement.industry, engagement.visit_date].filter(Boolean).join(' · ')}
          </div>
          <a
            href={publicReportPdfUrl(token)}
            target="_blank"
            rel="noreferrer"
            className="mt-6 inline-flex items-center gap-2.5 font-bold uppercase tracking-wider rounded-sm transition-all"
            style={{
              background: 'var(--gl-gold)',
              color: 'var(--gl-dark)',
              borderBottom: '4px solid #8e7223',
              padding: '14px 26px',
              fontSize: '1.05rem',
              letterSpacing: '0.1em',
              fontFamily: "'DM Serif Display','Inter',sans-serif",
              boxShadow: '0 10px 24px -10px rgba(191,153,46,0.6), 0 4px 12px -4px rgba(0,0,0,0.35)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 14px 32px -10px rgba(191,153,46,0.75), 0 6px 16px -4px rgba(0,0,0,0.45)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 10px 24px -10px rgba(191,153,46,0.6), 0 4px 12px -4px rgba(0,0,0,0.35)';
            }}
            data-testid="public-download-pdf"
          >
            <DownloadSimpleIcon size={20} weight="bold" /> Download Full PDF Report
          </a>
        </div>
        <div className="absolute right-0 bottom-0 w-full h-1" style={{ background: 'var(--gl-gold)' }} />
      </div>

      <div className="max-w-4xl mx-auto px-5 md:px-8 py-8 space-y-6">
        {/* Compliance score (if gap_check) */}
        {(report.kind === 'gap_check' || report.kind === 'combined') && gap_items && gap_items.length > 0 && (
          <ScoreCard score={report.compliance_score} gap_items={gap_items} />
        )}

        {/* Executive summary — first paragraph gets an editorial drop cap,
            subsequent paragraphs render plainly. */}
        <Section title="Executive Summary">
          <NarrativeProse text={report.executive_summary || '—'} />
        </Section>
        {/* Remediation narrative — auto-chunks into numbered step cards on
            the "First, Second, Third, ..., Finally" markers so it reads as a
            scannable action plan instead of a wall of prose. */}
        <Section title="Recommended Action Sequence">
          <ActionSequence text={report.remediation_narrative || '—'} />
        </Section>

        {/* Findings */}
        {(report.kind === 'walkthrough' || report.kind === 'combined') && findings && findings.length > 0 && (
          <Section title={`Findings · ${findings.length}`}>
            <div className="space-y-4">
              {findings.map((f) => (
                <PublicFinding key={f.id} f={f} media_index={media_index} token={token} />
              ))}
            </div>
          </Section>
        )}

        {/* Gap items */}
        {(report.kind === 'gap_check' || report.kind === 'combined') && gap_items && gap_items.length > 0 && (
          <Section title="Documentation Gap Check">
            <GapTable items={gap_items} />
          </Section>
        )}

        {/* GL-SPEC-APP-033 — Client-facing path-forward card. Derives the
            recommended next engagement from the compliance score + finding
            count + missing-doc count. Single CTA, single sentence of context,
            never a sales pitch. */}
        <WhatComesNext
          score={report.compliance_score}
          findings={findings}
          gap_items={gap_items}
          engagement={engagement}
        />

        {/* GL-SPEC-APP-030 — Additional documents shared with the client */}
        {attachments && attachments.length > 0 && (
          <Section title={`Additional Documents · ${attachments.length}`}>
            <AttachmentsList attachments={attachments} token={token} />
          </Section>
        )}

        {/* Disclaimer */}
        <div className="gl-card text-xs" style={{ color: 'var(--gl-muted)' }}>
          <div className="gl-label">Disclaimer</div>
          {settings?.disclaimer}
        </div>
      </div>

      <Footer footer={settings?.footer_contact} engagement={engagement} />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="gl-card gl-card-accent">
      <div
        className="px-3 py-2 -mx-4 -mt-4 mb-4 font-display font-extrabold uppercase tracking-widest"
        style={{ background: 'var(--gl-navy)', color: '#fff', letterSpacing: '0.08em' }}
      >
        {title}
      </div>
      {children}
    </section>
  );
}

function ScoreCard({ score, gap_items }) {
  const pct = score ?? 0;
  const color = pct >= 80 ? 'var(--gl-p3)' : pct >= 50 ? 'var(--gl-p2)' : 'var(--gl-p1)';
  const counts = gap_items.reduce(
    (acc, x) => {
      acc[x.status] = (acc[x.status] || 0) + 1;
      return acc;
    },
    { present_current: 0, outdated: 0, missing: 0 }
  );
  // GL-SPEC-APP-033 — Plain-language interpretation line so the client knows
  // what the number means without reading the executive summary first.
  const interpretation =
    pct >= 90
      ? 'Strong compliance posture. Maintain documentation cadence and close any remaining administrative gaps.'
      : pct >= 80
        ? 'Foundational documentation is in place. Critical physical and program gaps require attention before full production.'
        : pct >= 65
          ? 'Partial compliance. Several core programs require revision or implementation before next OSHA scrutiny.'
          : pct >= 50
            ? 'Material gaps across core programs. Prioritize a structured remediation plan within 60 days.'
            : 'Substantial compliance gap. Immediate intervention recommended across documentation and physical controls.';
  return (
    <div className="gl-card gl-card-accent">
      <div className="flex flex-col md:flex-row items-start md:items-end gap-4">
        <div>
          <div className="gl-label">Compliance Score</div>
          <div className="font-display font-black leading-none text-6xl md:text-7xl" style={{ color }}>
            {pct.toFixed(0)}<span className="text-2xl">%</span>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap md:ml-auto">
          <span className="gl-chip gl-chip-status-present">Present · {counts.present_current}</span>
          <span className="gl-chip gl-chip-status-outdated">Outdated · {counts.outdated}</span>
          <span className="gl-chip gl-chip-status-missing">Missing · {counts.missing}</span>
        </div>
      </div>
      <div
        className="mt-4 pt-3 border-t text-sm leading-relaxed"
        style={{
          borderColor: '#e5e7eb',
          color: 'var(--gl-dark)',
          fontFamily: "'Lora','Times New Roman',serif",
        }}
        data-testid="score-interpretation"
      >
        <span className="font-bold" style={{ color }}>What this means: </span>
        {interpretation}
      </div>
    </div>
  );
}

function PublicFinding({ f, media_index, token }) {
  return (
    <div
      className="border-l-4 p-4 rounded-sm"
      style={{
        borderColor: f.priority === 'P1' ? 'var(--gl-p1)' : f.priority === 'P2' ? 'var(--gl-p2)' : 'var(--gl-p3)',
        background: '#f9fafb',
      }}
      data-testid={`public-finding-${f.number}`}
    >
      <div className="flex items-start gap-3 flex-wrap">
        <PriorityChip value={f.priority} />
        <div className="font-display font-extrabold uppercase tracking-widest" style={{ color: 'var(--gl-dark)' }}>
          Finding #{f.number}
        </div>
        <div className="ml-auto text-xs uppercase tracking-widest font-bold" style={{ color: 'var(--gl-navy)' }}>
          {f.cfr_citation}
        </div>
      </div>
      <div className="font-bold mt-2" style={{ color: 'var(--gl-dark)' }}>
        {f.title || f.violation_type || 'Untitled finding'}
      </div>
      {f.standard_name && (
        <div className="text-xs uppercase tracking-widest font-bold mt-1" style={{ color: 'var(--gl-muted)' }}>
          {f.standard_name}
          {f.violation_type && f.title && f.violation_type !== f.title ? ` · ${f.violation_type}` : ''}
          {f.penalty_exposure && ` · Penalty: ${f.penalty_exposure}`}
        </div>
      )}
      {f.description && <p className="text-sm mt-2 whitespace-pre-wrap">{f.description}</p>}
      {f.corrective_action && (
        <div className="mt-2">
          <div className="gl-label" style={{ marginBottom: 4 }}>Corrective Action</div>
          <p className="text-sm whitespace-pre-wrap">{f.corrective_action}</p>
        </div>
      )}
      {f.video_url && (
        <div className="mt-3 flex items-center gap-3 flex-wrap p-2 border-2 rounded-sm bg-white" style={{ borderColor: 'var(--gl-navy)' }}>
          <QRCodeSVG value={f.video_url} size={72} />
          <div>
            <div className="text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--gl-navy)', fontFamily: 'Inter' }}>
              <VideoCameraIcon size={14} weight="bold" className="inline mr-1" /> Scan or open video
            </div>
            <a
              href={f.video_url}
              target="_blank"
              rel="noreferrer"
              className="text-sm break-all"
              style={{ color: 'var(--gl-navy)' }}
            >
              {f.video_url}
            </a>
          </div>
        </div>
      )}
      {(f.photo_ids || []).length > 0 && (
        <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-2">
          {f.photo_ids.map((pid) => {
            const m = media_index[pid];
            if (!m) return null;
            return (
              <figure key={pid} className="border-2 border-gray-200 rounded-sm overflow-hidden bg-white">
                <img src={publicMediaUrl(pid, token)} alt={m.caption || ''} className="w-full h-40 object-cover" />
                {m.caption && (
                  <figcaption className="text-xs p-1 text-center" style={{ color: 'var(--gl-muted)' }}>
                    {m.caption}
                  </figcaption>
                )}
              </figure>
            );
          })}
        </div>
      )}
    </div>
  );
}

function GapTable({ items }) {
  const groups = items.reduce((m, x) => {
    if (!m[x.category]) m[x.category] = [];
    m[x.category].push(x);
    return m;
  }, {});
  return (
    <div className="space-y-4">
      {Object.entries(groups).map(([cat, list]) => {
        const isConditional = list[0]?.is_conditional;
        const note = list[0]?.category_note;
        return (
          <div key={cat}>
            <div className="font-display font-extrabold uppercase tracking-widest mb-2 flex items-center gap-2 flex-wrap" style={{ color: 'var(--gl-dark)' }}>
              <span>{cat}</span>
              {isConditional && (
                <span className="gl-chip gl-chip-gold" style={{ padding: '0.2rem 0.5rem', fontSize: '0.65rem' }}>
                  CONDITIONAL
                </span>
              )}
            </div>
            {isConditional && note && (
              <div
                className="mb-2 p-2 border-l-4 text-xs"
                style={{ borderColor: 'var(--gl-gold)', background: '#fdf6e3', color: 'var(--gl-dark)' }}
              >
                {note}
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left" style={{ background: 'var(--gl-bg)' }}>
                    <th className="p-2 font-bold uppercase tracking-wider text-xs">Item</th>
                    <th className="p-2 font-bold uppercase tracking-wider text-xs">CFR</th>
                    <th className="p-2 font-bold uppercase tracking-wider text-xs">Status</th>
                    <th className="p-2 font-bold uppercase tracking-wider text-xs">Priority</th>
                    <th className="p-2 font-bold uppercase tracking-wider text-xs">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((g) => (
                    <tr key={g.id} className="border-t border-gray-200">
                      <td className="p-2">{g.item_label}</td>
                      <td className="p-2 text-xs whitespace-nowrap" style={{ color: 'var(--gl-navy)', fontFamily: 'Inter', fontWeight: 700, letterSpacing: '0.05em' }}>{g.cfr_citation}</td>
                      <td className="p-2">
                        <span
                          className={`gl-chip ${g.status === 'present_current' ? 'gl-chip-status-present' : g.status === 'outdated' ? 'gl-chip-status-outdated' : 'gl-chip-status-missing'}`}
                        >
                          {g.status === 'present_current' ? 'Present' : g.status === 'outdated' ? 'Incomplete' : 'Missing'}
                        </span>
                      </td>
                      <td className="p-2">
                        <PriorityChip value={g.priority || 'P3'} />
                      </td>
                      <td className="p-2 text-xs" style={{ color: 'var(--gl-muted)' }}>{g.notes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Footer({ footer, engagement }) {
  // crude parse for "phone (xxx) email"
  return (
    <footer
      className="px-5 md:px-8 py-8 text-sm text-white text-center"
      style={{ background: 'var(--gl-dark-navy)' }}
    >
      <div className="font-display font-extrabold uppercase tracking-widest text-lg" style={{ color: 'var(--gl-gold)' }}>
        GigLine Compliance
      </div>
      <p className="mt-2 text-xs opacity-80">{footer}</p>
      <div className="mt-4 flex justify-center gap-3 flex-wrap">
        <a href="tel:+13363298899" className="gl-btn gl-btn-accent" data-testid="footer-call">
          <PhoneIcon size={18} weight="bold" /> Call
        </a>
        <a href="mailto:vince@giglinecompliance.com" className="gl-btn gl-btn-secondary" data-testid="footer-email">
          <EnvelopeSimpleIcon size={18} weight="bold" /> Email
        </a>
      </div>
    </footer>
  );
}

// GL-SPEC-APP-030 — Public attachments list. Renders a card grid with file
// icon, name, size, and a single-click download button. Each download hits
// the public token-gated /api/public/reports/{token}/attachments/{id} endpoint.
function AttachmentsList({ attachments, token }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {attachments.map((a) => (
        <AttachmentCard key={a.id} a={a} token={token} />
      ))}
    </div>
  );
}

function AttachmentCard({ a, token }) {
  const href = `${API_BASE}/public/reports/${token}/attachments/${a.id}`;
  const ext = (a.filename || '').split('.').pop()?.toLowerCase() || '';
  const Icon = pickFileIcon(ext);
  const accent = pickFileAccent(ext);
  const sizeLabel = formatBytes(a.size_bytes || 0);

  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="gl-card flex items-center gap-4 transition hover:shadow-md"
      style={{ borderLeft: `4px solid ${accent}`, textDecoration: 'none' }}
      data-testid={`public-attachment-${a.id}`}
    >
      <div
        className="flex-shrink-0 flex items-center justify-center rounded-sm"
        style={{
          width: 48,
          height: 48,
          background: `${accent}22`,
          color: accent,
        }}
      >
        <Icon size={28} weight="duotone" />
      </div>
      <div className="flex-1 min-w-0">
        <div
          className="font-bold text-sm truncate"
          style={{ color: 'var(--gl-dark)' }}
          title={a.filename}
        >
          {a.filename}
        </div>
        <div className="text-xs mt-0.5" style={{ color: 'var(--gl-muted)' }}>
          {ext.toUpperCase() || 'FILE'} · {sizeLabel}
        </div>
      </div>
      <div
        className="flex-shrink-0 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider"
        style={{ color: 'var(--gl-dark)' }}
      >
        <DownloadSimpleIcon size={16} weight="bold" />
        <span className="hidden sm:inline">Download</span>
      </div>
    </a>
  );
}

function pickFileIcon(ext) {
  if (ext === 'pdf') return FilePdfIcon;
  if (ext === 'xlsx' || ext === 'xls' || ext === 'csv') return ext === 'csv' ? FileCsvIcon : FileXlsIcon;
  if (ext === 'docx' || ext === 'doc') return FileDocIcon;
  return FileIcon;
}

function pickFileAccent(ext) {
  if (ext === 'pdf') return '#C0392B';
  if (ext === 'xlsx' || ext === 'xls' || ext === 'csv') return '#1F7A4D';
  if (ext === 'docx' || ext === 'doc') return '#2A52A0';
  if (ext === 'png' || ext === 'jpg' || ext === 'jpeg') return '#BF992E';
  return '#4A6580';
}

function formatBytes(n) {
  if (!n) return '0 KB';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}



// GL-SPEC-APP-031 — Executive Summary prose with editorial drop cap on the
// first paragraph, matching the PDF's editorial styling.
function NarrativeProse({ text }) {
  const paragraphs = (text || '').split(/\n\n+/).map((p) => p.trim()).filter(Boolean);
  if (paragraphs.length === 0) return <p className="text-base leading-relaxed">—</p>;
  const [first, ...rest] = paragraphs;
  const dropChar = first.charAt(0).toUpperCase();
  const remainder = first.slice(1).replace(/^\s+/, '');
  return (
    <div>
      <p
        className="text-base leading-relaxed"
        style={{ fontFamily: "'Lora','Times','Times New Roman',serif" }}
      >
        <span
          className="float-left mr-3 mt-1 font-bold"
          style={{
            fontFamily: "'DM Serif Display','Times New Roman',serif",
            fontSize: '4.6em',
            lineHeight: 0.85,
            color: 'var(--gl-dark)',
            letterSpacing: '-0.02em',
          }}
        >
          {dropChar}
        </span>
        {remainder}
      </p>
      {rest.map((p, i) => (
        <p
          key={i}
          className="text-base leading-relaxed mt-4"
          style={{ fontFamily: "'Lora','Times','Times New Roman',serif" }}
        >
          {p}
        </p>
      ))}
    </div>
  );
}

// GL-SPEC-APP-031 — Auto-split a "First, ... Second, ... Finally, ..." prose
// narrative into numbered step cards so the public page mirrors the PDF's
// Recommended Action Sequence rendering. Markers are stripped from each
// step's body since the number badge replaces them.
const _ACTION_MARKERS = [
  'First,', 'Second,', 'Third,', 'Fourth,', 'Fifth,',
  'Sixth,', 'Seventh,', 'Eighth,', 'Ninth,', 'Tenth,',
  'Finally,', 'Lastly,',
];

function _splitNarrative(text) {
  if (!text || !text.trim()) return [];
  const positions = [];
  for (const marker of _ACTION_MARKERS) {
    let idx = 0;
    while (idx < text.length) {
      const found = text.indexOf(marker, idx);
      if (found === -1) break;
      const prev = found === 0 ? ' ' : text.charAt(found - 1);
      if (/\s/.test(prev) || found === 0 || /[.;!?]/.test(prev)) {
        positions.push({ idx: found, marker });
      }
      idx = found + marker.length;
    }
  }
  positions.sort((a, b) => a.idx - b.idx);
  if (positions.length < 2) return null;
  const chunks = [];
  for (let i = 0; i < positions.length; i++) {
    const start = positions[i].idx + positions[i].marker.length;
    const end = i + 1 < positions.length ? positions[i + 1].idx : text.length;
    const body = text.slice(start, end).trim();
    if (body) chunks.push(body);
  }
  return chunks;
}

function ActionSequence({ text }) {
  const steps = _splitNarrative(text);
  if (!steps || steps.length === 0) {
    return (
      <p className="text-base leading-relaxed" style={{ fontFamily: "'Lora',serif" }}>
        {text || '—'}
      </p>
    );
  }
  return (
    <ol className="space-y-3 list-none p-0 m-0">
      {steps.map((body, i) => (
        <li
          key={i}
          className="flex gap-3 md:gap-4 items-start rounded-sm"
          style={{
            background: i % 2 === 0 ? '#fafaf7' : '#ffffff',
            border: '1px solid #e5e7eb',
            padding: '14px 16px',
          }}
        >
          <span
            className="flex-shrink-0 inline-flex items-center justify-center font-bold text-sm"
            style={{
              width: 30,
              height: 30,
              background: 'var(--gl-navy)',
              color: '#ffffff',
              fontFamily: "'Inter',sans-serif",
              letterSpacing: '0.04em',
            }}
            data-testid={`action-step-${i + 1}`}
          >
            {i + 1}
          </span>
          <span
            className="flex-1 text-base leading-relaxed"
            style={{ fontFamily: "'Lora','Times New Roman',serif", color: 'var(--gl-dark)' }}
          >
            {body}
          </span>
        </li>
      ))}
    </ol>
  );
}

// GL-SPEC-APP-033 — Client-facing recommended next step. Mirrors the upsell
// engine's logic but only surfaces a SINGLE recommended path forward + a
// 30-day check-in confirmation. Never aggressive sales copy.
function _pickNextStep({ score, findings, gap_items }) {
  const s = score ?? 0;
  const missingDocs = (gap_items || []).filter((g) => g.status === 'missing').length;
  const p1Count = (findings || []).filter((f) => f.priority === 'P1').length;
  const p2Count = (findings || []).filter((f) => f.priority === 'P2').length;

  if (s < 65 || missingDocs >= 6) {
    return {
      service: 'OSHA-Ready Control System',
      price: 'from $4,500',
      reason:
        'With multiple core programs missing or incomplete, the OSHA-Ready Control System builds and installs every required written program, training record, and posting in one structured engagement.',
      cta_label: 'Book the OSHA-Ready Control System',
      cta_href: 'https://giglinecompliance.com/services/osha-ready',
    };
  }
  if (missingDocs >= 2 || s < 85) {
    return {
      service: 'Document Development',
      price: '$650–$1,500 per program',
      reason:
        'Close the specific document gaps identified above. Each program is built to the standard cited in the finding and delivered ready to roll out.',
      cta_label: 'Request a Document Development quote',
      cta_href: 'https://giglinecompliance.com/services/document-development',
    };
  }
  if (p1Count >= 1 || p2Count >= 3) {
    return {
      service: 'Compliance Readiness Visit',
      price: 'from $1,500',
      reason:
        'Close out the physical findings above with a focused on-site visit. Includes corrective-action verification and a follow-up scorecard.',
      cta_label: 'Schedule the Compliance Readiness Visit',
      cta_href: 'https://giglinecompliance.com/services/crv',
    };
  }
  return {
    service: 'Quarterly Compliance Maintenance',
    price: 'from $750 per quarter',
    reason:
      'Stay ahead. Quarterly check-ins keep documentation current and surface drift before it becomes a citation.',
    cta_label: 'Add Quarterly Maintenance',
    cta_href: 'https://giglinecompliance.com/services/quarterly',
  };
}

function WhatComesNext({ score, findings, gap_items }) {
  const next = _pickNextStep({ score, findings, gap_items });
  return (
    <Section title="What Comes Next">
      <div
        className="rounded-sm overflow-hidden"
        style={{ border: '1px solid var(--gl-gold)', background: '#fdfbf3' }}
        data-testid="public-next-step"
      >
        <div
          className="px-5 py-4"
          style={{ borderBottom: '1px solid var(--gl-gold)' }}
        >
          <div
            className="text-xs font-bold uppercase tracking-widest"
            style={{ color: 'var(--gl-gold)', fontFamily: "'Inter',sans-serif" }}
          >
            Recommended Next Engagement
          </div>
          <div
            className="text-2xl font-extrabold mt-1"
            style={{
              color: 'var(--gl-dark)',
              fontFamily: "'DM Serif Display','Times New Roman',serif",
            }}
          >
            {next.service}
          </div>
          <div className="text-sm mt-0.5" style={{ color: 'var(--gl-muted)' }}>
            {next.price}
          </div>
        </div>
        <div className="px-5 py-4">
          <p
            className="text-base leading-relaxed"
            style={{ color: 'var(--gl-dark)', fontFamily: "'Lora','Times New Roman',serif" }}
          >
            {next.reason}
          </p>
          <a
            href={next.cta_href}
            target="_blank"
            rel="noreferrer"
            className="mt-4 inline-flex items-center gap-2 font-bold uppercase tracking-wider rounded-sm transition-all"
            style={{
              background: 'var(--gl-dark-navy)',
              color: 'var(--gl-gold)',
              padding: '11px 22px',
              fontSize: '0.92rem',
              letterSpacing: '0.08em',
              fontFamily: "'Inter',sans-serif",
              boxShadow: '0 6px 18px -8px rgba(0,0,0,0.45)',
            }}
            data-testid="public-next-step-cta"
          >
            {next.cta_label} →
          </a>
        </div>
        <div
          className="px-5 py-3 text-sm flex items-start gap-2"
          style={{ background: 'var(--gl-dark-navy)', color: '#e8dec3' }}
        >
          <span style={{ color: 'var(--gl-gold)' }}>●</span>
          <span>
            <strong style={{ color: '#fff' }}>30-day check-in included.</strong> GigLine will
            follow up in 30 days to confirm priority items have been addressed and to surface any
            new conditions that emerged during remediation.
          </span>
        </div>
      </div>
    </Section>
  );
}

